/* eslint-disable @angular-eslint/component-selector, @angular-eslint/no-output-rename, @angular-eslint/no-input-rename, @angular-eslint/no-outputs-metadata-property, @angular-eslint/no-inputs-metadata-property, @typescript-eslint/member-ordering, @typescript-eslint/no-non-null-assertion, @angular-eslint/directive-selector, @typescript-eslint/no-empty-function, jsdoc/require-jsdoc, @angular-eslint/no-empty-lifecycle-method, @typescript-eslint/ban-ts-comment */
import { FlexibleConnectedPositionStrategy, Overlay, ConnectedPosition } from '@angular/cdk/overlay';
import { CdkScrollable } from '@angular/cdk/scrolling';
import {
    CdkEditable,
    CdkEditControl,
    CdkPopoverEdit,
    EditEventDispatcher,
    EditRef,
} from '@angular/cdk-experimental/popover-edit';
import {
    AfterContentInit,
    Component,
    Directive,
    Injectable,
    InjectionToken,
    Input,
    NgZone,
    OnInit,
    inject,
} from '@angular/core';
import { MatMenuTrigger } from '@angular/material/menu';
import { WINDOW } from '@ng-web-apis/common';
import { fromEvent, NEVER } from 'rxjs';
import { delay, first, switchMap, take, tap } from 'rxjs/operators';
import { ConfigService } from '@mona/config';
import { PLATFORM } from '@mona/shared/utils';
import { UiVHSTableComponent } from '../components';

const POPOVER_EDIT_HOST_BINDINGS = {
    '[attr.tabindex]': 'disabled ? null : 0',
    class: 'mat-popover-edit-cell',
    '[attr.aria-haspopup]': '!disabled',
};
const POPOVER_EDIT_INPUTS = [
    'template: uiPopoverEdit',
    'context: uiPopoverEditContext',
    // 'colspan: uiPopoverEditColspan',
    'disabled: uiPopoverEditDisabled',
];
const EDIT_PANE_CLASS = 'mat-edit-pane';
export const CELL_SELECTOR = '.cdk-cell, .mat-mdc-cell, td';

/** Interface for position strategy factory */
export interface PositionStrategyFactory {
    positionStrategyForCells(cells: HTMLElement[]): FlexibleConnectedPositionStrategy;
}

/** Injection token for position strategy factory */
export const POSITION_STRATEGY_FACTORY = new InjectionToken<PositionStrategyFactory>('POSITION_STRATEGY_FACTORY');

/** Default position strategy factory implementation */
@Injectable()
export class DefaultPositionStrategyFactory implements PositionStrategyFactory {
    constructor(private overlay: Overlay) {}

    positionStrategyForCells(cells: HTMLElement[]): FlexibleConnectedPositionStrategy {
        const positions: ConnectedPosition[] = [
            { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'top' },
            { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom' },
            { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top' },
            { originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom' },
        ];

        return this.overlay
            .position()
            .flexibleConnectedTo(cells[0])
            .withPositions(positions)
            .withPush(true)
            .withFlexibleDimensions(false);
    }
}

// -----------------------------------------------------------------------------
// OVERRIDE CDK EDIT DIRECTIVES
// merge-resolved: 2.4.5 cell-edit
// -----------------------------------------------------------------------------

/**
 * Custom {@link EditEventDispatcher} which doesn't track row hover events
 * @override
 */
@Injectable()
// @ts-ignore // to override hover observers
export class UiVHSEditEventDispatcher extends EditEventDispatcher<any> {
    override registerRowWithHoverContent(row: Element): void {}
    override deregisterRowWithHoverContent(row: Element): void {}
    override hoverOrFocusOnRow(row: Element) {
        return NEVER;
    }
    private override _hoveredContentStateDistinct = NEVER;
}

/**
 * Enables CDK popover-edit on a table and swaps in our hover-less EditEventDispatcher.
 * Uses hostDirectives so CDK can provide its private EditServices token (not publicly exported in v19).
 * Ceiling: host CdkEditable attaches table event listeners; hover is neutralized via UiVHSEditEventDispatcher.
 */
@Directive({
    selector: 'table[ui-editable]',
    hostDirectives: [CdkEditable],
    providers: [{ provide: EditEventDispatcher, useClass: UiVHSEditEventDispatcher }],
    standalone: false,
})
export class UiVHSEditable {}

/**
 * Attaches an ng-template to a cell and shows it when instructed to by the
 * EditEventDispatcher service.
 * Makes the cell focusable.
 *
 * Implements multiple overrides to handle keyboard events & repositioning
 * @override
 */
@Directive({
    selector: '[uiPopoverEdit]',
    host: POPOVER_EDIT_HOST_BINDINGS,
    inputs: POPOVER_EDIT_INPUTS,
    standalone: false,
})
// @ts-ignore // to override `_getPositionStrategy`
export class UiVHSPopoverEdit<C> extends CdkPopoverEdit<C> {
    @Input() uiPopoverDouble = false;

    private cdkScrollable: CdkScrollable;
    platform = inject(PLATFORM);
    private configService = inject(ConfigService);

    constructor() {
        super();
    }

    protected override panelClass(): string {
        return EDIT_PANE_CLASS;
    }

    override ngAfterViewInit(): void {
        super.ngAfterViewInit();
        // assign scrollable container to override `_getPositionStrategy`
        this.cdkScrollable = this.viewContainerRef.injector.get(UiVHSTableComponent)?.leftTableWrapper;
    }

    private override _getPositionStrategy(): FlexibleConnectedPositionStrategy {
        // Get our custom position factory from the injector
        const positionFactory = this.viewContainerRef.injector.get(POSITION_STRATEGY_FACTORY);
        const positionStrategy = positionFactory
            // @ts-ignore // to access member
            .positionStrategyForCells(this._getOverlayCells()) as FlexibleConnectedPositionStrategy;

        return positionStrategy.withScrollableContainers([this.cdkScrollable]);
    }

    private override _createEditOverlay(): void {
        this.configService
            .select('isBedSide')
            .pipe(
                tap(isOskOn => {
                    const isElectronAndOskOn = this.platform.isElectron && isOskOn;
                    const activeCellRangeToBottom =
                        window.innerHeight -
                        (this._getPositionStrategy()._origin as HTMLElement).getBoundingClientRect().bottom;
                    const keyboardHeight = 384;
                    const shouldAddMarginForFloatingCell =
                        isElectronAndOskOn && activeCellRangeToBottom < keyboardHeight;

                    this.overlayRef = this.services.overlay.create({
                        disposeOnNavigation: true,
                        panelClass: [
                            this.uiPopoverDouble ? 'double' : '',
                            'mat-edit-pane',
                            shouldAddMarginForFloatingCell ? 'resize-on-keyboard-top' : '',
                        ],
                        positionStrategy: this._getPositionStrategy(),
                        scrollStrategy: this.services.overlay.scrollStrategies.block(),
                        direction: this.services.directionality,
                    });

                    this.initFocusTrap();
                    this.overlayRef.overlayElement.setAttribute('aria-role', 'dialog');
                }),
                switchMap(() => this.overlayRef.detachments()),
                take(1),
            )
            .subscribe(() => this.closeEditOverlay());
    }
}

/**
 * A component that attaches to a form within the edit.
 * It coordinates the form state with the table-wide edit system and handles
 * closing the edit when the form is submitted or the user clicks
 * out.
 *
 * Implements multiple overrides to handle keyboard events & repositioning
 * @override
 */
@Directive({
    selector: 'form[uiEditLens], ui-form[uiEditLens]',
    host: {
        class: 'mat-edit-lens',
    },
    inputs: [
        'clickOutBehavior: uiEditLensClickOutBehavior',
        'preservedFormValue: uiEditLensPreservedFormValue',
        'ignoreSubmitUnlessValid: uiEditLensIgnoreSubmitUnlessValid',
    ],
    outputs: ['preservedFormValueChange: uiEditLensPreservedFormValueChange'],
    providers: [EditRef],
    standalone: false,
})
export class VhsTableEditLens<FormValue> extends CdkEditControl<FormValue> implements OnInit {
    /** @internal */
    private ngZone = inject(NgZone);
    protected window = inject(WINDOW);

    constructor() {
        super();
    }

    /** @internal */
    override ngOnInit() {
        super.ngOnInit();
        //  Watch for keyboard hide event to close the edit
        this.ngZone.runOutsideAngular(() => {
            fromEvent(this.window, 'keyboard:hide')
                .pipe(first(), delay(0))
                .subscribe(() => {
                    this.editRef.close();
                });
        });
    }

    /**
     * Called on click anywhere in the document.
     * If the click was outside of the lens, trigger the specified click out behavior.
     * @override
     * @param evt
     */
    override handlePossibleClickOut(evt: Event): void {
        // check if active element is form input or textarea or keyboard key
        if (
            !!(document.activeElement as HTMLInputElement).form ||
            document.activeElement.classList.contains('mat-keyboard-key')
        ) {
            return;
        }
        super.handlePossibleClickOut(evt);
    }
}

/**
 * Empty comonent derived from {@link MatMenuTrigger} directive to create dynamic element that should trigger a `mat-menu`.
 * @override
 */
@Component({
    template: '',
    selector: `uiMenuTriggerFor`,
    host: {
        class: 'mat-menu-trigger',
    },
    inputs: [
        'menu:uiMenuTriggerFor',
        'matMenuTriggerData:uiMenuTriggerData',
        'matMenuTriggerRestoreFocus:uiMenuTriggerRestoreFocus',
    ],
    exportAs: 'uiMenuTrigger',
    standalone: false,
})
export class UiVHSMatMenuTrigger extends MatMenuTrigger implements AfterContentInit {
    override ngAfterContentInit() {
        // skip
    }
}
